import { createSelector } from '@reduxjs/toolkit';
import type Item from '../@types/item';
import { parseRetailer } from '../models/retailer';
import type { RootState } from '../reducers';
import { resolveRouteRetailer } from '../shared/resolve-routes';

export const getRetailers = createSelector(
  (state: RootState) => state.retailers.documentList,
  (documentList) => {
    if (!documentList) {
      return [];
    }

    return documentList.documents.filter((d) => !!d).map(parseRetailer);
  },
);

export const getSearchRetailers = createSelector(
  (state: RootState) => state.retailers.documentList,
  (documentList) => {
    if (!documentList) {
      return [];
    }

    return documentList.documents.filter((d) => !!d);
  },
);

export const getRetailerDocument = createSelector(
  (state: RootState) => state.retailers.single,
  (documentList) =>
    documentList && documentList.documents && documentList.documents.length === 1
      ? documentList.documents[0]
      : null,
);

export const getRetailerId = createSelector(getRetailerDocument, (retailer) =>
  retailer && retailer.id ? retailer.id : null,
);

export const getRetailerPromos = createSelector(getRetailerDocument, (retailer) =>
  retailer && retailer.promos ? retailer.promos : [],
);

export const getRetailerName = createSelector(getRetailerDocument, (retailer) =>
  retailer && retailer.name ? retailer.name : null,
);

export const getRetailerLocaiton = createSelector(
  getRetailerDocument,
  (_state: RootState, item?: Item | null) => item,
  (retailer, item) => {
    if (retailer && retailer.position && retailer.position.location) {
      return {
        lat: retailer.position.location.lat,
        lng: retailer.position.location.lon,
      };
    } else if (item && item.position && item.position.location) {
      return {
        ...item.position.location,
      };
    }

    return null;
  },
);

export const getRetailerOpeningHours = createSelector(getRetailerDocument, (retailer) =>
  retailer && retailer.openingHours ? retailer.openingHours : null,
);

export const getRetailerTelephone = createSelector(getRetailerDocument, (retailer) =>
  retailer && retailer.telephoneNumber ? retailer.telephoneNumber : null,
);

export const getRetailerAddress = createSelector(getRetailerDocument, (retailer) =>
  retailer && retailer.position && retailer.position.street && retailer.position.city
    ? `${retailer.position.street}, ${retailer.position.city}`
    : null,
);

export const getRetailerAbout = createSelector(getRetailerDocument, (retailer) =>
  retailer
    ? {
        name: retailer.name,
        logo: retailer.logo,
        city: retailer.position && retailer.position.city ? retailer.position.city : null,
        description: retailer.description,
        link: `${resolveRouteRetailer()}/${retailer.slug}`,
      }
    : null,
);

export const isRetailerUsingFreespee = createSelector(
  getRetailerDocument,
  (retailer) =>
    !!retailer &&
    retailer.telephoneNumberGenerationSettings &&
    retailer.telephoneNumberGenerationSettings.active,
);
